import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const AcceptInvitation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token');

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      console.log('Loading invitation with token:', token);
      const { data, error: inviteError } = await supabase
        ?.from('team_invitations')
        ?.select('*, companies(company_name)')
        ?.eq('token', token)
        ?.eq('status', 'pending')
        ?.single();

      console.log('Invitation query result:', { data, error: inviteError });

      if (inviteError) {
        console.error('Error loading invitation:', inviteError);
        // Check if it's a permission error (RLS)
        if (inviteError.code === 'PGRST301' || inviteError.message?.includes('permission') || inviteError.message?.includes('policy')) {
          setError('Unable to access invitation. Please contact support.');
        } else {
          setError('Invitation not found or has expired');
        }
        setLoading(false);
        return;
      }

      if (!data) {
        console.error('No invitation data returned');
        setError('Invitation not found or has expired');
        setLoading(false);
        return;
      }

      // Check if invitation has expired
      if (new Date(data?.expires_at) < new Date()) {
        setError('This invitation has expired');
        setLoading(false);
        return;
      }

      setInvitation(data);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e?.preventDefault();
    setError('');

    if (!fullName?.trim()) {
      setError('Full name is required');
      return;
    }

    if (!password || password?.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      // Sign up the user
      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email: invitation?.email,
        password: password,
        options: {
          data: {
            full_name: fullName,
            company_id: invitation?.company_id,
            role: invitation?.role
          }
        }
      });

      if (signUpError) throw signUpError;

      // Wait for user profile to be created by trigger
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update user profile with company info
      if (authData?.user?.id) {
        const { error: updateError } = await supabase
          ?.from('user_profiles')
          ?.update({
            company_id: invitation?.company_id,
            role: invitation?.role
          })
          ?.eq('id', authData?.user?.id);

        if (updateError) {
          console.error('Error updating profile:', updateError);
        }
      }

      // Mark invitation as accepted
      const { error: inviteUpdateError } = await supabase
        ?.from('team_invitations')
        ?.update({ status: 'accepted' })
        ?.eq('id', invitation?.id);

      if (inviteUpdateError) {
        console.error('Error updating invitation:', inviteUpdateError);
      }

      // Redirect to login
      navigate('/login?message=Account created successfully. Please sign in.');
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err?.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-lg p-6 md:p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid Invitation</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} variant="default">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-lg p-6 md:p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="Mail" size={32} className="text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Accept Invitation</h1>
          <p className="text-muted-foreground">
            You've been invited to join <strong>{invitation?.companies?.company_name}</strong> as a{' '}
            <strong>{invitation?.role}</strong>
          </p>
        </div>

        <form onSubmit={handleAccept} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={invitation?.email}
            disabled
            className="bg-muted/50"
          />

          <Input
            label="Full Name"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e?.target?.value)}
            placeholder="Enter your full name"
            required
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e?.target?.value)}
            placeholder="Create a password (min 8 characters)"
            required
          />

          <Input
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e?.target?.value)}
            placeholder="Confirm your password"
            required
          />

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            loading={submitting}
            className="w-full"
          >
            Accept Invitation & Create Account
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-accent hover:underline font-medium"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;